/**
 * Meanbook Note - PDF Scanner & Auto-Fill Tracking Module
 * รองรับการอ่านแยกเลขพัสดุจากใบเสร็จขนส่ง (PDF) และจับคู่ออเดอร์อัตโนมัติ
 */

import { doc, writeBatch, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase.js";
import { hasRole } from "./auth.js";
import { getCarrierSettings, detectCarrier, detectCarriers } from "./carrier.js";
import { showToast, escapeHTML } from "./uiHelpers.js";

let getActiveNoteId = () => null;
let getAllOrders = () => [];

let pdfjsLoadingPromise = null;

export function loadPdfJs() {
  if (window.pdfjsLib) {
    return Promise.resolve(window.pdfjsLib);
  }
  if (pdfjsLoadingPromise) return pdfjsLoadingPromise;

  pdfjsLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js";
    script.async = true;
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js";
        resolve(window.pdfjsLib);
      } else {
        reject(new Error("pdfjsLib not available after script load"));
      }
    };
    script.onerror = () => {
      pdfjsLoadingPromise = null;
      reject(new Error("ไม่สามารถโหลดไลบรารีอ่านไฟล์ PDF ได้"));
    };
    document.head.appendChild(script);
  });
  return pdfjsLoadingPromise;
}

export function updatePDFUploadButtonVisibility() {
  const activeNoteId = getActiveNoteId();
  const btnUploadPdf = document.getElementById("btn-upload-pdf");
  if (btnUploadPdf) {
    if (activeNoteId === "shipping-queue" && hasRole(["Super Admin", "Shipper"])) {
      btnUploadPdf.style.display = "inline-flex";
    } else {
      btnUploadPdf.style.display = "none";
    }
  }
  updateActionBarVisibility();
}

export function updateActionBarVisibility() {
  const activeNoteId = getActiveNoteId();
  const actionBar = document.getElementById("board-action-bar-div");
  if (!actionBar) return;

  const boardMeta = actionBar.querySelector(".board-meta");
  const addOrderBtn = document.getElementById("btn-add-order");
  const btnUploadPdf = document.getElementById("btn-upload-pdf");

  const isQueue = [
    "cleanup-queue",
    "undo-queue",
    "prepare-queue",
    "shipping-queue",
    "pickup-queue",
    "completed-queue"
  ].includes(activeNoteId);

  if (activeNoteId === null) {
    actionBar.style.display = "none";
    return;
  }

  // Restore defaults for normal notes
  if (boardMeta) boardMeta.style.display = "flex";
  if (addOrderBtn) addOrderBtn.style.display = "inline-flex";
  actionBar.style.padding = "";
  if (btnUploadPdf) {
    btnUploadPdf.style.width = "";
    btnUploadPdf.style.justifyContent = "";
    btnUploadPdf.style.marginLeft = "8px";
    btnUploadPdf.style.backgroundColor = "var(--color-primary)";
    btnUploadPdf.style.boxShadow = "";
  }

  if (isQueue) {
    // Show action bar only in shipping-queue for Super Admin or Shipper to access PDF upload button
    if (activeNoteId === "shipping-queue" && hasRole(["Super Admin", "Shipper"])) {
      if (boardMeta) boardMeta.style.display = "none";
      if (addOrderBtn) addOrderBtn.style.display = "none";

      // Make PDF button full width, centered, and green!
      if (btnUploadPdf) {
        btnUploadPdf.style.width = "100%";
        btnUploadPdf.style.justifyContent = "center";
        btnUploadPdf.style.marginLeft = "0px";
        btnUploadPdf.style.backgroundColor = "var(--color-success)"; // Green theme color
        btnUploadPdf.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.2)";
      }

      actionBar.style.display = "flex";
      actionBar.style.padding = "10px 14px"; // Sleek padding
    } else {
      actionBar.style.display = "none";
    }
  } else {
    actionBar.style.display = "flex";
  }
}

export function normalizeName(str) {
  if (!str) return "";
  return str
    .normalize("NFC")
    // ปรับ สระอำ ทั้งหมด (ทั้งแบบ \u0e4d\u0e32 นฤคหิต+สระอา และแบบมีวรรณยุกต์คั่น) ให้เป็น \u0e33 สระอำมาตรฐาน
    .replace(/\u0e4d\s*([\u0e48-\u0e4b]?)\s*\u0e32/g, "$1\u0e33")
    .replace(/([\u0e48-\u0e4b])\s*\u0e4d\s*\u0e32/g, "$1\u0e33")
    // ลบ Null byte, zero-width, วรรณยุกต์ไทย (่้๊๋), ทัณฑฆาต/การันต์ (์), นฤคหิต (ํ), ไม้ไต่คู้ (็)
    .replace(/[\u0000่้๊๋์็ํ๎ฺ\u200B-\u200D\uFEFF]/g, "")
    // ลบเครื่องหมายวรรคตอนและวงเล็บต่างๆ
    .replace(/[()[\]{}.,:;"'\-_/\\+=!@#$%^&*~`|]/g, "")
    .replace(/\s+/g, "")
    .replace(/^(คุณ|นาย|นางสาว|นาง|ด\.ช\.|ด\.ญ\.)/g, "")
    .toLowerCase();
}

export function findTrackingCandidates(text) {
  const words = text.match(/[A-Z0-9-]{9,25}/gi) || [];
  const candidates = [];
  const settings = getCarrierSettings();

  const allPrefixes = [
    ...(settings.ems || []),
    ...(settings.flash || []),
    ...(settings.spx || []),
    ...(settings.dhl || []),
    ...(settings.kex || []),
    ...(settings.other || [])
  ].map(p => p.trim().toUpperCase()).filter(p => p.length > 0);

  for (const word of words) {
    const cleanWord = word.replace(/-/g, "").toUpperCase();
    let hasPrefix = allPrefixes.some(pref => cleanWord.startsWith(pref));

    let isStandardEMS = /^[A-Z]{2}\d{9}[A-Z]{2}$/.test(cleanWord);
    let isStandardFlash = /^TH\d{12}$/.test(cleanWord);
    let isStandardSPX = /^SPXTH\d{10,15}$/.test(cleanWord);
    let isStandardKEX = /^(KEX|KER)\d{9,12}$/.test(cleanWord);

    if (hasPrefix || isStandardEMS || isStandardFlash || isStandardSPX || isStandardKEX) {
      if (!candidates.includes(cleanWord)) {
        candidates.push(cleanWord);
      }
    }
  }
  return candidates;
}

export async function handlePDFUpload(file) {
  showToast("กำลังเริ่มอ่านไฟล์ PDF...", "info");

  let pdfjs = null;
  try {
    pdfjs = await loadPdfJs();
  } catch (err) {
    console.error("PDF.js loading failed:", err);
    showToast(err.message, "danger");
    return;
  }

  const reader = new FileReader();
  reader.onload = async function () {
    try {
      const typedarray = new Uint8Array(this.result);
      const pdf = await pdfjs.getDocument(typedarray).promise;

      let allTextLines = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const items = textContent.items;

        // Group items by Y coordinate (nearest 4px)
        const linesMap = {};
        for (const item of items) {
          const y = Math.round(item.transform[5] / 4) * 4;
          if (!linesMap[y]) {
            linesMap[y] = [];
          }
          linesMap[y].push(item);
        }

        // Sort keys descending
        const sortedY = Object.keys(linesMap).sort((a, b) => b - a);

        for (const y of sortedY) {
          const lineItems = linesMap[y].sort((a, b) => a.transform[4] - b.transform[4]);
          const lineText = lineItems.map(item => item.str).join(" ");
          if (lineText.trim()) {
            allTextLines.push(lineText.trim());
          }
        }
      }

      console.log("Extracted PDF text lines:", allTextLines);
      processPDFTextLines(file, allTextLines);

    } catch (err) {
      console.error("PDF Parsing Error:", err);
      showToast("ไม่สามารถประมวลผลไฟล์ PDF ได้ กรุณาตรวจสอบว่าเป็นไฟล์ PDF ที่มีข้อความธรรมดา", "danger");
    }
  };
  reader.readAsArrayBuffer(file);
}

export function processPDFTextLines(file, allTextLines) {
  const allOrders = getAllOrders();
  const shipQueueOrders = allOrders.filter(o => o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express');

  if (shipQueueOrders.length === 0) {
    showToast("ไม่มีออเดอร์รอลงเลขพัสดุในระบบ 🚚", "warning");
    return;
  }

  const trackingMatches = [];
  for (let i = 0; i < allTextLines.length; i++) {
    const candidates = findTrackingCandidates(allTextLines[i]);
    for (const c of candidates) {
      if (!trackingMatches.some(tm => tm.tracking === c)) {
        trackingMatches.push({ tracking: c, lineIndex: i });
      }
    }
  }

  console.log("Tracking candidates found:", trackingMatches);

  const matchedResults = {};
  const matchedPool = new Set();

  // 1. Phone number match (clean symbols/formatting)
  for (const tm of trackingMatches) {
    const lineIndex = tm.lineIndex;
    const trackingVal = tm.tracking;

    const startIdx = Math.max(0, lineIndex - 4);
    const endIdx = Math.min(allTextLines.length - 1, lineIndex + 4);
    const proximityText = allTextLines.slice(startIdx, endIdx + 1).join(" ");
    const cleanProximityDigits = proximityText.replace(/\D/g, "");

    for (const order of shipQueueOrders) {
      if (matchedPool.has(order.id)) continue;

      const cleanPhone = order.phone ? order.phone.replace(/\D/g, "").replace(/^66/, "0") : "";
      if (cleanPhone.length >= 9 && cleanProximityDigits.includes(cleanPhone)) {
        matchedResults[order.id] = trackingVal;
        matchedPool.add(order.id);
        console.log(`Phone match: ${order.customerName} -> ${trackingVal}`);
        break;
      }
    }
  }

  // 2. Name match (Fuzzy title and spacing matching)
  for (const tm of trackingMatches) {
    if (Object.values(matchedResults).includes(tm.tracking)) continue;

    const lineIndex = tm.lineIndex;
    const trackingVal = tm.tracking;

    const startIdx = Math.max(0, lineIndex - 4);
    const endIdx = Math.min(allTextLines.length - 1, lineIndex + 4);
    const proximityText = allTextLines.slice(startIdx, endIdx + 1).join(" ");
    const normProximityText = normalizeName(proximityText);

    for (const order of shipQueueOrders) {
      if (matchedPool.has(order.id)) continue;

      // ฟังก์ชันช่วยเปรียบเทียบชื่อแบบยืดหยุ่น (Fuzzy Match แยกส่วนคำย่อย)
      const matchFuzzy = (targetName) => {
        if (!targetName) return false;
        
        // 1. ลองแมตช์ตรงตัวตามปกติ (หลัง normalize)
        const normTarget = normalizeName(targetName);
        if (normTarget && normProximityText.includes(normTarget)) return true;
        
        // 2. แมตช์แบบแบ่งคำย่อย (แยกชื่อ-นามสกุล และตัดเครื่องหมายวรรคตอน)
        const parts = targetName
          .split(/[\s()[\]{}.,:;"'\-_/\\+=!@#$%^&*~`|]+/)
          .map(normalizeName)
          .filter(p => p.length > 1);

        if (parts.length === 0) return false;
        if (parts.every(part => normProximityText.includes(part))) return true;

        // กรณีมีชื่อ-นามสกุล และ 2 คำแรกตรงกับใบเสร็จ (เช่น ชื่อ+นามสกุลตรง แม้ในระบบจะมีข้อความสาขา/โน้ตต่อท้าย)
        if (parts.length >= 2 && parts.slice(0, 2).every(part => normProximityText.includes(part))) {
          return true;
        }

        return false;
      };

      if (matchFuzzy(order.customerName) || matchFuzzy(order.thaiName)) {
        matchedResults[order.id] = trackingVal;
        matchedPool.add(order.id);
        console.log(`Name match: ${order.customerName} -> ${trackingVal}`);
        break;
      }
    }
  }

  openPDFSplitOverlay(file, matchedResults);
}

export function openPDFSplitOverlay(file, matchedResults) {
  const overlay = document.getElementById("pdf-split-overlay");
  const iframe = document.getElementById("pdf-viewer-frame");
  const matchedContainer = document.getElementById("pdf-matched-cards-list");

  if (!overlay || !iframe || !matchedContainer) return;

  const fileURL = URL.createObjectURL(file);
  iframe.src = fileURL;

  const allOrders = getAllOrders();
  const shipQueueOrders = allOrders.filter(o => o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express');

  const totalCountEl = document.getElementById("pdf-total-orders-count");
  if (totalCountEl) totalCountEl.textContent = shipQueueOrders.length;

  matchedContainer.innerHTML = "";

  let successCount = 0;

  shipQueueOrders.forEach(order => {
    const trackingNumber = matchedResults[order.id] || "";
    const isMatched = !!trackingNumber;
    if (isMatched) successCount++;

    const carrierObj = trackingNumber ? detectCarrier(trackingNumber) : null;
    const carrierName = carrierObj ? carrierObj.name : "";

    const row = document.createElement("div");
    row.className = `pdf-matched-card-row ${isMatched ? 'matched' : 'unmatched'}`;
    row.setAttribute("data-order-id", order.id);

    row.style.background = "var(--bg-card)";
    row.style.border = `2px solid ${isMatched ? 'rgba(16, 185, 129, 0.4)' : 'rgba(245, 158, 11, 0.4)'}`;
    row.style.borderRadius = "12px";
    row.style.padding = "14px";
    row.style.display = "flex";
    row.style.flexDirection = "column";
    row.style.gap = "8px";
    row.style.boxShadow = "0 4px 12px rgba(0,0,0,0.04)";
    row.style.transition = "all 0.2s ease";
    row.style.borderLeft = `5px solid ${isMatched ? 'var(--color-success)' : 'var(--color-warning)'}`;

    row.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: flex-start;">
        <div>
          <div style="font-weight: 700; font-size: 0.9rem; color: var(--color-text-main);">${escapeHTML(order.customerName || 'ลูกค้า')}</div>
          <div style="font-size: 0.75rem; color: var(--color-text-muted); margin-top: 2px;">
            โทร: ${escapeHTML(order.phone || 'ไม่มีเบอร์โทร')} | โน้ต: ${escapeHTML(order.noteTitle || '')}
          </div>
        </div>
        <span class="platform-tag" style="background-color: var(--color-primary-light); color: var(--color-primary); font-size: 0.7rem; font-weight: 700; padding: 2px 6px; border-radius: 4px;">
          ${escapeHTML(order.platform || 'General')}
        </span>
      </div>
      
      <div style="font-size: 0.75rem; color: var(--color-text-muted); background: rgba(0,0,0,0.05); padding: 6px 10px; border-radius: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${escapeHTML(order.items || '')}">
        <strong>สินค้า:</strong> ${escapeHTML(order.items || 'ไม่ได้ระบุ')}
      </div>
      
      <div style="display: flex; gap: 10px; margin-top: 4px; align-items: center;">
        <div style="flex: 2; position: relative;">
          <span style="font-size: 0.7rem; font-weight: 700; color: var(--color-text-muted); display: block; margin-bottom: 4px;">เลขพัสดุ (Tracking Number)</span>
          <input type="text" class="form-control pdf-tracking-input" data-order-id="${order.id}" value="${escapeHTML(trackingNumber)}" placeholder="ระบุเลขพัสดุ..." style="width: 100%; font-size: 0.85rem; font-weight: 600; padding: 6px 10px; border-radius: 8px; border: 1px solid var(--border-color); background: var(--bg-app); color: var(--color-text-main);">
        </div>
        
        <div style="flex: 1.2;">
          <span style="font-size: 0.7rem; font-weight: 700; color: var(--color-text-muted); display: block; margin-bottom: 4px;">ผู้ขนส่ง (Carrier)</span>
          <select class="form-control pdf-carrier-select" data-order-id="${order.id}" style="width: 100%; font-size: 0.8rem; font-weight: 600; padding: 6px 6px; border-radius: 8px; border: 1px solid var(--border-color); background: var(--bg-app); color: var(--color-text-main);">
            <option value="">-- ออโต้ --</option>
            <option value="EMS" ${carrierName === 'EMS' ? 'selected' : ''}>EMS 📬</option>
            <option value="Flash" ${carrierName === 'Flash' ? 'selected' : ''}>Flash ⚡</option>
            <option value="SPX" ${carrierName === 'SPX' ? 'selected' : ''}>SPX 🧡</option>
            <option value="DHL" ${carrierName === 'DHL' ? 'selected' : ''}>DHL 🔴</option>
            <option value="KEX" ${carrierName === 'KEX' ? 'selected' : ''}>KEX 📦</option>
            <option value="อื่นๆ" ${carrierName === 'อื่นๆ' ? 'selected' : ''}>อื่นๆ 🌐</option>
          </select>
        </div>
      </div>
      
      <div class="pdf-carrier-detect-badge" id="pdf-carrier-detect-${order.id}" style="font-size: 0.72rem; color: var(--color-text-muted); font-weight: 600; min-height: 16px; display: flex; align-items: center; gap: 4px;">
        <!-- Populated dynamically -->
      </div>
    `;

    matchedContainer.appendChild(row);

    const trackingInput = row.querySelector(".pdf-tracking-input");
    const carrierSelect = row.querySelector(".pdf-carrier-select");

    const updateInlineBadge = () => {
      const trackingVal = trackingInput.value.trim();
      const selectedCarrier = carrierSelect.value;
      const badgeDiv = row.querySelector(`.pdf-carrier-detect-badge`);

      if (!trackingVal) {
        badgeDiv.innerHTML = `<span style="color: var(--color-text-muted);">กรุณากรอกเลขพัสดุ</span>`;
        row.style.border = `2px solid rgba(245, 158, 11, 0.4)`;
        row.style.borderLeft = `5px solid var(--color-warning)`;
        return;
      }

      const carriers = detectCarriers(trackingVal);
      let displayCarrier = selectedCarrier;

      if (!displayCarrier) {
        if (carriers.length > 0) {
          displayCarrier = carriers[0].name;
        } else {
          displayCarrier = "อื่นๆ";
        }
      }

      if (carriers.length === 0) {
        badgeDiv.innerHTML = `ตรวจพบขนส่ง: <span style="color: var(--color-text-muted); font-weight: 700;">อื่นๆ 🌐</span>`;
      } else {
        const matchingCarrier = carriers.find(c => c.name === displayCarrier) || carriers[0];
        badgeDiv.innerHTML = `ตรวจพบขนส่ง: <span style="color: #38bdf8; font-weight: 700; display: inline-flex; align-items: center; gap: 3px;"><i data-lucide="${matchingCarrier.icon}" style="width:12px; height:12px;"></i> ${matchingCarrier.label}</span>`;
      }

      row.style.border = `2px solid rgba(16, 185, 129, 0.4)`;
      row.style.borderLeft = `5px solid var(--color-success)`;

      if (window.lucide) window.lucide.createIcons();
    };

    trackingInput.addEventListener("input", updateInlineBadge);
    carrierSelect.addEventListener("change", updateInlineBadge);

    updateInlineBadge();
  });

  const statsBadge = document.getElementById("pdf-match-stats-badge");
  if (statsBadge) {
    statsBadge.innerHTML = `
      <i data-lucide="check-circle" style="width: 14px; height: 14px;"></i>
      <span>จับคู่สำเร็จ ${successCount}/${shipQueueOrders.length} ออเดอร์</span>
    `;
  }

  overlay.style.display = "flex";
  if (window.lucide) window.lucide.createIcons();
}

export function closePDFSplitOverlay() {
  const overlay = document.getElementById("pdf-split-overlay");
  const iframe = document.getElementById("pdf-viewer-frame");
  if (overlay) overlay.style.display = "none";
  if (iframe) iframe.src = "";
}

export async function saveAllPDFMatchedOrders() {
  const allOrders = getAllOrders();
  const matchedContainer = document.getElementById("pdf-matched-cards-list");
  if (!matchedContainer) return;

  const rows = matchedContainer.querySelectorAll(".pdf-matched-card-row");
  if (rows.length === 0) return;

  showToast("กำลังบันทึกเลขพัสดุลงระบบ...", "info");

  let updateCount = 0;
  const batch = writeBatch(db);

  for (const row of rows) {
    const orderId = row.getAttribute("data-order-id");
    const trackingInput = row.querySelector(".pdf-tracking-input");
    const carrierSelect = row.querySelector(".pdf-carrier-select");

    if (!orderId || !trackingInput || !carrierSelect) continue;

    const order = allOrders.find(o => o.id === orderId);
    if (!order) continue;

    const trackingVal = trackingInput.value.trim();
    if (!trackingVal) continue;

    const carriers = detectCarriers(trackingVal);
    let finalCarrier = carrierSelect.value;
    if (!finalCarrier) {
      if (carriers.length > 0) {
        finalCarrier = carriers[0].name;
      } else {
        finalCarrier = "อื่นๆ";
      }
    }

    const orderRef = doc(db, "orders", orderId);
    batch.update(orderRef, {
      trackingNumber: trackingVal,
      carrier: finalCarrier,
      isPacked: true,
      updatedAt: serverTimestamp(),
      ...( (!order.trackingNumber && trackingVal) ? { completedAt: serverTimestamp() } : {} )
    });
    updateCount++;
  }

  if (updateCount === 0) {
    showToast("ไม่มีข้อมูลเลขพัสดุที่จะบันทึก", "warning");
    return;
  }

  try {
    await batch.commit();
    showToast(`บันทึกเลขพัสดุสำเร็จ ${updateCount} ออเดอร์! 🚚`, "success");
    closePDFSplitOverlay();
  } catch (err) {
    console.error("Batch save tracking error:", err);
    showToast("เกิดข้อผิดพลาดในการบันทึกเลขพัสดุ", "danger");
  }
}

export function setupPdfScanner(options = {}) {
  if (options.getActiveNoteId) getActiveNoteId = options.getActiveNoteId;
  if (options.getAllOrders) getAllOrders = options.getAllOrders;

  const btnUploadPdf = document.getElementById("btn-upload-pdf");
  const pdfFileInput = document.getElementById("pdf-file-input");
  if (btnUploadPdf && pdfFileInput) {
    btnUploadPdf.addEventListener("click", () => {
      pdfFileInput.click();
    });
    pdfFileInput.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (file) {
        handlePDFUpload(file);
      }
      pdfFileInput.value = "";
    });
  }

  const btnPdfClose = document.getElementById("btn-pdf-close");
  if (btnPdfClose) {
    btnPdfClose.addEventListener("click", closePDFSplitOverlay);
  }

  const btnPdfSaveAll = document.getElementById("btn-pdf-save-all");
  if (btnPdfSaveAll) {
    btnPdfSaveAll.addEventListener("click", saveAllPDFMatchedOrders);
  }
}

// Backward compatibility bindings for global access
if (typeof window !== "undefined") {
  window.closePDFSplitOverlay = closePDFSplitOverlay;
  window.saveAllPDFMatchedOrders = saveAllPDFMatchedOrders;
}
